import {
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { HttpErrorResponse } from '@angular/common/http';
import {
  DOCUMENT_STATE,
  DocumentModalResult,
} from 'src/app/product-capture/model/consts/document-modal.consts';
import { LANGUAGE_LIST } from 'src/app/product-capture/model/consts/language-list.const';
import { DOCUMENT_FILE_TYPE, DocumentSubType } from 'src/app/product-capture/model/enums/document-file-type.enum';
import { DOCUMENT_STATUS } from 'src/app/product-capture/model/enums/document-status.enum';
import { DocumentFile } from 'src/app/product-capture/model/interfaces/document-file.interface';
import { DropDownOption } from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { ProductCaptureData } from 'src/app/product-capture/model/interfaces/product-capture.interface';
import { DocService } from 'src/app/product-capture/services/doc.service';
import { ModalService } from 'src/app/ui-kit/dc-modal/modal.service';
import { WorkflowMessageContext } from 'src/app/workflows/workflow-message-handler/workflow-handler.const';
import { WorkflowMessageHandler } from 'src/app/workflows/workflow-message-handler/workflow-message-handler';
import { DOCUMENT_TYPE } from '../../../model/consts/document-type.const';
import { DocumentActionType } from '../../../model/enums/document-action-type.enum';
import { DOCUMENT_FILE_TYPE_ID } from '../../../model/enums/document-file-type-id.enum';
import { LANGUAGE } from './../../../model/enums/language.enum';
import { CompareModalService } from './compare-modal/compare-modal.service';
import { DOCUMENTS_TAB_COLUMNS } from './documents-tab-columns.const';
import { getDocErrorMessage } from './getDocErrorMessage';
import { PreviewModalService } from './preview-modal/preview-modal.service';
import { RequestModalService } from './request-modal/request-modal.service';
import { UploadModalService } from './upload-modal/upload-modal.service';

@Component({
  selector: 'app-documents-tab',
  templateUrl: './documents-tab.component.html',
  styleUrls: ['./documents-tab.component.scss'],
})
export class DocumentsTabComponent extends WorkflowMessageHandler implements OnInit {

  @Input()
  productId: number;

  @Input()
  productDetails: ProductCaptureData;

  columns = DOCUMENTS_TAB_COLUMNS;
  documentTypes = DOCUMENT_FILE_TYPE;
  loading = true;
  docType = DOCUMENT_TYPE[0].id;
  messages = {
    totalMessage: 'Total',
  };
  rows: DocumentFile[] = [];
  lastVersions: DocumentFile[] = [];
  sortOrder = [
    {
      prop: 'createdOn',
    },
  ];

  types = DOCUMENT_TYPE;

  @ViewChild('subtype', { static: true })
  subtypeTemplate: TemplateRef<any>;

  constructor(
    public modalService: ModalService,
    private docService: DocService,
    private uploadModalService: UploadModalService,
    private requestModalService: RequestModalService,
    private compareModalService: CompareModalService,
    private previewModalService: PreviewModalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchDocuments();
  }

  downloadAll(): void {
    this.docService.downloadAll(this.productId);
  }

  download(docId: number): void {
    this.docService.download(docId);
  }

  async doAction(document: DocumentFile, action: string): Promise<void> {
    switch (action) {
      case DocumentActionType.DELETE:
        this.openDeleteModal(document);
        break;
      case DocumentActionType.DOWNLOAD:
        this.download(document.id);
        break;
      case DocumentActionType.APPROVE:
        await this.openApproveModal(document);
        break;
      case DocumentActionType.REJECT:
        this.openRejectModal(document);
        break;
      case DocumentActionType.COMPARE:
        this.openCompareModal(document);
        break;
      case DocumentActionType.PREVIEW:
        this.openPreviewModal(document);
        break;
      default:
        break;
    }
  }

  getFileType(fileName: string): string {
    if (
      fileName === DOCUMENT_FILE_TYPE_ID.SPREAD_SHEET ||
      fileName === DOCUMENT_FILE_TYPE_ID.FINAL_TERMS
    ) {
      return 'document-spreadsheet';
    }

    return 'document-text';
  }

  getTypeName(type: string): string {
    return this.documentTypes[type];
  }

  getStatusLabel(status: DOCUMENT_STATUS): string {
    return status.toLowerCase().replace(' ', '-');
  }

  getFileName(fileName: string): string {
    const paths = fileName?.split('/') || [fileName];
    return paths[paths.length - 1];
  }

  setType(type: DropDownOption): void {
    this.docType = type.id;

    this.columns = [...DOCUMENTS_TAB_COLUMNS];
    if (type.displayValue === DOCUMENT_FILE_TYPE.OTHER) {
      this.columns.splice(1, 0, {
        name: 'Type',
        prop: 'subtype',
        resizeable: false,
        draggable: false,
        minWidth: 200,
        canAutoResize: true,
        flexGrow: 1,
        cellTemplate: this.subtypeTemplate,
      });

      this.columns = [...this.columns];
    }
    this.fetchDocuments();
  }

  openRequestNewModal(): void {
    this.requestModalService
      .open({
        docType: this.docType,
        productType: this.productType
      })
      .then(
        (result) => {
          if (result !== 'cancel') {
            this.request(result);
          }
        },
        (error) => {
          console.log(error);
        }
      );
  }

  openUploadModal(): void {
    this.uploadModalService.open({}).then(
      (result) => {
        if (result !== 'cancel') {
          const { type, language, documentState, file } = result;
          this.upload(type, language, documentState, file.file);
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private openDeleteModal(document: DocumentFile): void {
    this.modalService
      .openDialog({
        title: `Remove document ${this.getFileName(document.fileUrl)}`,
        body: `<p>Are you sure you want to delete this document?</p>`,
        buttons: {
          positive: 'Remove',
          negative: 'Cancel',
        },
        size: 'md',
      })
      .then(
        (result) => {
          if (result === '{"positive":1}') {
            this.delete(document);
          }
          console.log(result);
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private openRejectModal(document: DocumentFile): void {
    this.modalService
      .openDialog({
        title: `Reject document ${this.getFileName(document.fileUrl)}`,
        body: `<p>Are you sure you want to reject this document?</p>`,
        buttons: {
          positive: 'Reject',
          negative: 'Cancel',
        },
        size: 'md',
      })
      .then(
        (result) => {
          if (result === '{"positive":1}') {
            this.reject(document);
          }
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private async openApproveModal(document: DocumentFile): Promise<void> {
    try {
      const result = await this.modalService
      .openDialog({
        title: `Approve document ${this.getFileName(document.fileUrl)}`,
        body: `<p>Are you sure you want to approve this document?</p>`,
        buttons: {
          positive: 'Approve',
          negative: 'Cancel',
        },
        size: 'md',
      });

      if (result === '{"positive":1}') {
        this.approve(document);
      }
    } catch (error) {
      console.log(error);
    }
  }

  private async openCompareModal(document: DocumentFile): Promise<void> {
    try {
      await this.compareModalService.open({ document: document, rows: this.rows });
    } catch (error) {
      console.log(error);
    }
  }

  private async openPreviewModal(document: DocumentFile): Promise<void> {
    try {
      await this.previewModalService.open({ documentId: document.id, format: document.fileFormat });   
    } catch (error) {
      console.error({ error });
    }
  }

  private delete(document: DocumentFile): void {
    this.loading = true;

    this.docService.delete(document.id).subscribe(
      () => {
        this.dcAlertInnerService.showAlert({
          type: 'success',
          message: `File ${this.getFileName(
            document.fileUrl
          )} successfully deleted.`,
        });
        this.fetchDocuments();
      },
      (error) => {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: getDocErrorMessage(error),
        });
        this.loading = false;
      }
    );
  }

  private approve(document: DocumentFile): void {
    this.loading = true;

    this.docService.approve(document.id).subscribe(
      () => {
        this.dcAlertInnerService.showAlert({
          type: 'success',
          message: `File ${this.getFileName(
            document.fileUrl
          )} successfully approved.`,
        });
        this.fetchDocuments();
      },
      (error) => {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: getDocErrorMessage(error),
        });
        this.loading = false;
      }
    );
  }

  private reject(document: DocumentFile): void {
    this.loading = true;

    this.docService.reject(document.id).subscribe(
      () => {
        this.dcAlertInnerService.showAlert({
          type: 'success',
          message: `File ${this.getFileName(
            document.fileUrl
          )} successfully reject.`,
        });
        this.fetchDocuments();
      },
      (error) => {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: getDocErrorMessage(error),
        });
        this.loading = false;
      }
    );
  }

  private request(result: DocumentModalResult): void {
    this.loading = true;

    const { language, docFormat, type, documentState, subtype } = result;

    this.docService
      .request(
        this.productId,
        language,
        documentState,
        type || this.docType,
        docFormat,
        subtype
      )
      .subscribe(
        () => {
          this.dcAlertInnerService.showAlert({
            type: 'success',
            message: `Document successfully requested.`,
          });
          this.fetchDocuments();
        },
        (error: HttpErrorResponse) => {
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: getDocErrorMessage(error),
          });
          this.loading = false;
        }
      );
  }

  private async upload(
    type: string,
    language: LANGUAGE,
    documentState: string,
    file: File
  ): Promise<void> {
    this.loading = true;
    const base64doc = await this.docService.file2Base64(file);

    this.docService
      .upload(
        type,
        language,
        documentState,
        this.productId,
        file,
        base64doc,
        this.isin
      )
      .subscribe(
        () => {
          this.dcAlertInnerService.showAlert({
            type: 'success',
            message: `File successfully uploaded.`,
          });
          this.fetchDocuments();
        },
        (error) => {
          this.dcAlertInnerService.showAlert({
            type: 'danger',
            message: getDocErrorMessage(error),
          });
          this.loading = false;
        }
      );
  }

  fetchDocuments(): void {
    this.loading = true;

    this.docService.fetchDocs(this.docType, this.productId).subscribe(
      (docs) => {
        const documentState = 'documentState';
        const languageLabel = 'languageLabel';
        const fileFormat = 'fileFormat';
        this.lastVersions = [];

        docs.sort(
          (a, b) =>
            Number(new Date(b.createdAt).getTime()) -
            Number(new Date(a.createdAt).getTime())
        );

        this.rows = docs.map((doc) => {
          doc[languageLabel] =
            LANGUAGE_LIST.find((language) => language.id === doc.language)
              ?.displayValue || doc.language;

          doc[documentState] =
            DOCUMENT_STATE.find(
              (documentState) => documentState.id === doc.documentState
            )?.displayValue || doc.documentState;

          doc[fileFormat] = this.getFileName(doc.fileUrl)
            ?.match(/\.([^.]+)$/)?.[1]
            .toUpperCase();

          // For TMG, these columns are not needed
          if (doc.subtype === DocumentSubType.TMG) {
            doc = {
              ...doc,
              status: '-',
              documentState: '-',
              fileFormat: '-',
            }
          }

          return doc;
        });

        this.rows.forEach((doc) => {
          const hasDoc = this.lastVersions.some(
            (version) => version.language === doc.language
          );

          if (!hasDoc) {
            this.lastVersions.push(doc);
          }
        });
      },
      (error) => {
        this.dcAlertInnerService.showAlert({
          type: 'danger',
          message: getDocErrorMessage(error),
        });
      },
      () => {
        this.loading = false;
      }
    );
  }

  refreshData(): void {
    this.fetchDocuments();
  }

  get context(): WorkflowMessageContext {
    return WorkflowMessageContext.DOCUMENT;
  }

  get productType(): string {
    return this.productDetails?.summary?.productType;
  }

  get isin(): string {
    return this.productDetails?.summary?.isin;
  }
}
